/**
 * Derives thumbnail sources from a campaign row
 * Prefers 16:9 aspect ratio, then 4:3, then others
 * Returns up to 3 unique thumbnail URLs
 *
 * @param row - Campaign row data containing screens and creatives
 * @returns Array of up to 3 thumbnail URLs
 */

import {
  CREATIVE_TYPE,
  normalizeCreativeType,
} from '@/shared/constants/creativeTypes';

export const VAST_THUMBNAIL =
  'https://images.justbaat.com/static/vast-thumbnail.png';

export const WEBVIEW_THUMBNAIL =
  'https://images.justbaat.com/static/web-thumbnail.png';

export const deriveCampaignThumbnails = (row: any): string[] => {
  try {
    const screens: any[] = Array.isArray(row?.screens) ? row.screens : [];
    const creatives: any[] = Array.isArray(row?.creatives) ? row.creatives : [];

    if (screens.length === 0) return [];

    const firstScreen = screens[0];
    const slots: any[] = Array.isArray(firstScreen?.slots)
      ? firstScreen.slots
      : [];

    // HANDLE VAST / WEBVIEW FIRST

    for (const slot of slots) {
      const slotCreatives: any[] = Array.isArray(slot?.creatives)
        ? slot.creatives
        : [];

      for (const slotCreative of slotCreatives) {
        const type = slotCreative?.type || '';
        const normalizedType = normalizeCreativeType(type);

        if (normalizedType === CREATIVE_TYPE.VAST) {
          return [VAST_THUMBNAIL];
        }

        if (normalizedType === CREATIVE_TYPE.WEB) {
          return [WEBVIEW_THUMBNAIL];
        }
      }
    }

    // Collect thumbnails from top-level creatives array (new shape: src is a direct URL string)
    const candidateFiles: Array<{ aspectRatio?: string; src?: string }> = [];

    for (let i = 0; i < Math.min(creatives.length, 5); i++) {
      const creative = creatives[i];
      if (creative?.src && typeof creative.src === 'string') {
        candidateFiles.push({
          aspectRatio: creative.aspectRatio,
          src: creative.src,
        });
      }
    }

    if (candidateFiles.length === 0) return [];

    // Prefer 16:9, then 4:3, then others; keep unique srcs
    const preferredOrder = ['16:9', '4:3'];
    const uniqueSrcs = Array.from(
      new Set(candidateFiles.map(f => f.src as string))
    );

    const pickByAspect = (ar: string) =>
      candidateFiles
        .filter(f => String(f.aspectRatio).toLowerCase() === ar.toLowerCase())
        .map(f => f.src as string);

    const ordered: string[] = [];
    preferredOrder.forEach(ar => {
      pickByAspect(ar).forEach(src => {
        if (!ordered.includes(src)) ordered.push(src);
      });
    });
    // Append remaining unique srcs
    uniqueSrcs.forEach(src => {
      if (!ordered.includes(src)) ordered.push(src);
    });

    return ordered.slice(0, 5);
  } catch {
    return [];
  }
};
